// 📁 src/admin/Enquiries.js
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../axiosClient";
import moment from "moment";
import { useEnquiryContext } from "../context/EnquiryContext";

const Enquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const { refreshNewCount } = useEnquiryContext();

  const fetchEnquiries = async () => {
    try {
      const res = await api.get("/api/enquiries");
      setEnquiries(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch enquiries:", err);
    } finally {
      setLoading(false);
    }
  };

  const markAsSeen = async (id) => {
    try {
      await api.patch(`/api/enquiries/${id}/seen`);
      setEnquiries((prev) =>
        prev.map((enq) => (enq._id === id ? { ...enq, isNew: false } : enq))
      );
      refreshNewCount();
    } catch (err) {
      console.error("Failed to update enquiry:", err);
    }
  };

  const updateStatus = async (id, status) => {
  try {
    const res = await api.patch(`/api/enquiries/${id}/status`, { status });
    setEnquiries((prev) => prev.map((e) => (e._id === id ? res.data : e)));
    refreshNewCount(); // sidebar badge stays accurate
  } catch (err) {
    console.error("Failed to update status:", err);
  }
};

  useEffect(() => {
    fetchEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => enquiries, [enquiries]);

  if (loading) return <p className="p-6">Loading export enquiries...</p>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#105132]">Export Enquiries</h1>
          <p className="text-sm text-gray-600 mt-1">
            Total: <span className="font-semibold">{rows.length}</span>
          </p>
        </div>

        <button
          onClick={fetchEnquiries}
          className="px-4 py-2 rounded-lg bg-white border border-gray-200 shadow-sm hover:shadow transition"
        >
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#105132] text-white">
              <tr>
                <th className="p-3 text-left font-semibold">Name</th>
                <th className="p-3 text-left font-semibold">Email</th>
                <th className="p-3 text-left font-semibold">Phone</th>
                <th className="p-3 text-left font-semibold">Country</th>
                <th className="p-3 text-left font-semibold">Product</th>
                <th className="p-3 text-left font-semibold">Quantity</th>
                <th className="p-3 text-left font-semibold">Message</th>
                <th className="p-3 text-left font-semibold">Date</th>
                <th className="p-3 text-left font-semibold">Status</th>
                <th className="p-3 text-right font-semibold">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {rows.map((e) => {
                const preview =
                  (e.message || "").length > 60
                    ? `${e.message.slice(0, 60)}…`
                    : e.message || "-";

                return (
                  <tr key={e._id} className="hover:bg-gray-50">
                    <td className="p-3 font-medium text-gray-900">{e.fullName}</td>
                    <td className="p-3 text-gray-700">{e.email}</td>

                    {/* ✅ phone fallback (works even if field name differs) */}
                    <td className="p-3 text-gray-700">
                      {e.phone || e.phoneNumber || e.mobile || "-"}
                    </td>

                    <td className="p-3 text-gray-700">{e.country}</td>
                    <td className="p-3 text-gray-700">{e.product}</td>
                    <td className="p-3 text-gray-700">{e.quantity || "-"}</td>

                    {/* ✅ message preview + view button */}
                    <td className="p-3 text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700">{preview}</span>
                        {e.message && e.message.length > 60 && (
                          <button
                            onClick={() => setSelected(e)}
                            className="tpx-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:shadow-sm transition text-xs"
                          >
                            View
                          </button>
                        )}
                      </div>
                    </td>

                    <td className="p-3 text-xs text-gray-600">
                      {moment(e.createdAt).format("DD MMM YYYY, hh:mm A")}
                    </td>

                    {/* ✅ always show status */}
                    <td className="p-3">
                      <select
                        value={e.status || (e.isNew ? "New" : "Contacted")}
                        onChange={(ev) => updateStatus(e._id, ev.target.value)}
                        className="border border-gray-200 rounded-lg px-2 py-1 bg-white text-sm"
                      >
                        <option value="New">New</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Quoted">Quoted</option>
                        <option value="Closed">Closed</option>
                      </select>
                    </td>

                    {/* ✅ always show something in action */}
                    <td className="p-3 text-right">
                      {e.isNew ? (
                        <button
                          onClick={() => markAsSeen(e._id)}
                          className="text-xs bg-[#105132] text-white px-3 py-2 rounded-lg hover:opacity-95 transition"
                        >
                          Mark as Read
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}

              {rows.length === 0 && (
                <tr>
                  <td colSpan="10" className="p-6 text-center text-gray-500">
                    No enquiries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ✅ Modal for full message */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 bg-[#f3f0e4]">
              <div>
                <h3 className="text-lg font-semibold text-[#105132]">
                  {selected.fullName}
                </h3>
                <p className="text-sm text-gray-600">{selected.email}</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-gray-600 hover:text-gray-900 text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="p-5 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Info label="Phone" value={selected.phone || selected.phoneNumber || selected.mobile || "-"} />
                <Info label="Country" value={selected.country || "-"} />
                <Info label="Product" value={selected.product || "-"} />
                <Info label="Quantity" value={selected.quantity || "-"} />
              </div>

              <div className="pt-2">
                <p className="text-xs font-semibold text-gray-600">Message</p>
                <div className="mt-1 border border-gray-200 rounded-xl p-4 bg-white whitespace-pre-line text-gray-800">
                  {selected.message || "-"}
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Submitted on{" "}
                {selected.createdAt
                  ? moment(selected.createdAt).format("DD MMM YYYY, hh:mm A")
                  : "-"}
              </p>
            </div>

            <div className="px-5 py-4 flex justify-end gap-2 border-t border-gray-100">
              {selected.isNew && (
                <button
                  onClick={() => {
                    markAsSeen(selected._id);
                    setSelected(null);
                  }}
                  className="px-4 py-2 rounded-lg bg-[#105132] text-white hover:opacity-95 transition"
                >
                  Mark as Read
                </button>
              )}
              <button
                onClick={() => setSelected(null)}
                className="px-4 py-2 rounded-lg bg-white border border-gray-200 hover:shadow-sm transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Info = ({ label, value }) => (
  <div className="border border-gray-200 rounded-xl p-3 bg-white">
    <p className="text-xs font-semibold text-gray-600">{label}</p>
    <p className="text-sm font-medium text-gray-900 mt-1">{value}</p>
  </div>
);

export default Enquiries;
